import { request } from './client';

export const FUND_AUTO_DRAFT_CONSENT_VERSION = 'fund-auto-drafts-v1';

export type FundRole = 'investor' | 'portfolio' | 'neither';
export type FundContact = {
  counterparty_id: string;
  display_name: string;
  kind: 'person' | 'organization';
  email: string | null;
  role: FundRole | null;
  stage: string | null;
  stage_set_by?: 'ai' | 'manual_override' | null;
  stage_reason?: string | null;
  next_step?: string | null;
  follow_up_on?: string | null;
  institution_id?: string | null;
  set_by: string | null;
  confirmed_by: string | null;
  suggested_role: 'investor' | 'portfolio' | null;
  suggested_source?: 'model' | 'model_auto' | 'model_review' | 'model_unknown'
    | 'model_neither' | 'company_investor' | null;
  suggested_reason?: string | null;
  suggested_organization_name?: string | null;
  suggested_organization_domain?: string | null;
  sample_subjects: string[];
  last_seen: string;
  automated_address?: boolean;
  personal_email_domain?: boolean;
};
export type FundCandidate = {
  email: string;
  name: string;
  subjects: string[];
  thread_count: number;
  last_seen: string;
  suggested_role: FundRole | null;
};
export type FundDocument = {
  document_id: string;
  category: 'fund' | 'collateral';
  title: string;
  filename: string;
  size_bytes: number;
  uploaded_by: string;
  created_at: string;
};
export type FundOutputSource = {
  number: number; document_id: string; title: string; filename: string; excerpt_sha256?: string;
};
export type FundOutputProvenance = {
  model_id: string; prompt_version: string; generated_at: string;
  evidence: { number: number; owner_email: string; message_id: string; subject: string; sent_at: string; excerpt_sha256: string }[];
  notes: { number: number; note_id: string; title: string; updated_at: string; excerpt_sha256: string }[];
};
export type FundWorkspace = {
  profile: {
    manager_name: string;
    funds: { name: string; strategy: string }[];
    sector_focus: string[];
    target_lp_types: string[];
  };
  preferences: { include_personal_emails: boolean;
    auto_drafts: { enabled: boolean; consent_version: string;
      consented_at: string | null; updated_at: string | null };
    auto_draft_consent_version: string };
  counterparties: FundContact[];
  institutions: { institution_id: string; name: string; contact_count: number;
    stage: string | null; stage_set_by?: 'ai' | 'manual_override' | null;
    stage_reason?: string | null; next_step: string | null; follow_up_on: string | null }[];
  documents: FundDocument[];
  provider: 'google' | 'microsoft';
  inbox_connected: boolean;
};
export type FundProfile = FundWorkspace['profile'];
export type FundInstitutionMessage = {
  counterparty_id: string; display_name: string; email: string; owner_email: string;
  message_id: string; thread_id: string; subject: string; sent_at: string; sender: string;
};

export const getFundWorkspace = () => request<FundWorkspace>('/fund/workspace');
export const undoFundReviewRole = (id: string, role: FundRole) =>
  request<{ ok: boolean }>(`/fund/contacts/${encodeURIComponent(id)}/undo-review`, {
    method: 'POST', body: JSON.stringify({ role }),
  });
export type FundTodayDecision = { counterparty_id: string; display_name: string;
  kind: 'respond' | 'chase'; subject: string; sent_at: string; message_id: string; age_days: number;
  ready_draft?: { subject: string; body: string; source_message_id: string;
    source_manifest: { evidence?: { message_id: string; subject: string }[] };
    generated_at: string } };
export const getFundTodayDecisions = () =>
  request<{ decisions: FundTodayDecision[] }>('/fund/today/decisions');
export type FundTodayCall = { id: string; summary: string; start: string;
  investors: Array<{ counterparty_id: string; display_name: string }> };
export const getFundTodayCalls = (start: string, end: string) =>
  request<{ events: FundTodayCall[] }>(`/fund/today/calls?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
export const actOnFundTodayDecision = (decision: FundTodayDecision, action: 'dismiss' | 'snooze') =>
  request<{ saved: boolean }>(`/fund/today/decisions/${encodeURIComponent(decision.counterparty_id)}/action`, {
    method: 'POST', body: JSON.stringify({ kind: decision.kind,
      source_message_id: decision.message_id, action }),
  });
export const getFundDashboardViews = () =>
  request<{ view_states: Record<string, Record<string, unknown>> }>('/fund/dashboard/views');
export type FundMailActivity = Record<string, { inbound: string | null; outbound: string | null }>;
export const getFundDashboardMailActivity = () =>
  request<{ activity: FundMailActivity }>('/fund/dashboard/mail-activity');
export const saveFundDashboardView = (key: string, state: Record<string, unknown>) =>
  request<{ saved: boolean }>(`/fund/dashboard/views/${encodeURIComponent(key)}`, {
    method: 'PUT', body: JSON.stringify({ state }),
  });
export const deleteFundDashboardView = (key: string) =>
  request<{ deleted: boolean }>(`/fund/dashboard/views/${encodeURIComponent(key)}`, { method: 'DELETE' });
export type FundInstitutionBrief = {
  brief: string | null;
  source_manifest?: { contact_ids: string[]; sources: Array<{ number: number; type: string; contact_name: string; subject?: string; title?: string }>; documents: FundOutputSource[] };
  generated_at?: string;
  review_only?: boolean;
};
export const getFundInstitutionBrief = (id: string) =>
  request<FundInstitutionBrief>(`/fund/institutions/${encodeURIComponent(id)}/brief`);
export const generateFundInstitutionBrief = (id: string, contactIds: string[], instruction = '', documentIds: string[] = []) =>
  request<FundInstitutionBrief>(`/fund/institutions/${encodeURIComponent(id)}/brief`, {
    method: 'POST', body: JSON.stringify({ contact_ids: contactIds, instruction, document_ids: documentIds }),
  });
export type FundPin = { counterparty_id?: string; entity_key?: string; entry_contact_id?: string; display_name: string; stage: string | null };
export const getFundPins = () => request<{ pinned: FundPin[] }>('/fund/pins');
export const pinFundContact = (id: string) =>
  request<{ pinned: boolean }>(`/fund/pins/${encodeURIComponent(id)}`, { method: 'POST' });
export const unpinFundContact = (id: string) =>
  request<{ pinned: boolean }>(`/fund/pins/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const pinFundOrganization = (key: string) =>
  request<{ pinned: boolean }>(`/fund/pins/organizations/${encodeURIComponent(key)}`, { method: 'POST' });
export const unpinFundOrganization = (key: string) =>
  request<{ pinned: boolean }>(`/fund/pins/organizations/${encodeURIComponent(key)}`, { method: 'DELETE' });
export const saveFundProfile = (profile: FundProfile) => request<FundProfile>('/fund/profile', {
  method: 'PUT', body: JSON.stringify(profile),
});
export const setFundPersonalEmails = (include: boolean) =>
  request<{ ok: boolean }>('/fund/preferences', { method: 'POST',
    body: JSON.stringify({ include_personal_emails: include }) });
export const setFundAutoDrafts = (enabled: boolean, consentVersion: string) =>
  request<{ enabled: boolean; consent_version: string; consented_at: string | null;
    updated_at: string | null }>('/fund/preferences/auto-drafts', { method: 'PUT',
    body: JSON.stringify({ enabled, consent_version: consentVersion }) });
export const getFundContext = (id: string) =>
  request<{ outputs: { brief?: string; draft?: string }; output_sources: { brief?: FundOutputSource[]; draft?: FundOutputSource[] }; output_provenance?: { brief?: FundOutputProvenance; draft?: FundOutputProvenance }; notes: FundNote[]; messages: { message_id: string; thread_id: string; subject: string; sent_at: string; sender: string; owner_email: string; evidence_number: number | null; excerpt: string }[] }>(
    `/fund/contacts/${encodeURIComponent(id)}/context`);
export const addFundNote = (id: string, title: string, body: string) =>
  request<{ note_id: string }>(`/fund/contacts/${encodeURIComponent(id)}/notes`, {
    method: 'POST', body: JSON.stringify({ title, body }),
  });
export const editFundNote = (id: string, noteId: string, title: string, body: string) =>
  request<{ updated: boolean }>(`/fund/contacts/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, {
    method: 'PUT', body: JSON.stringify({ title, body }),
  });
export const getFundMessage = (id: string, messageId: string, ownerEmail: string) =>
  request<{ subject: string; sent_at: string; sender: string; body: string }>(
    `/fund/contacts/${encodeURIComponent(id)}/evidence/${encodeURIComponent(messageId)}?owner_email=${encodeURIComponent(ownerEmail)}`);
export const refreshFundThreads = (id: string) =>
  request<{ updated: number; remaining: number }>(`/fund/contacts/${encodeURIComponent(id)}/threads/refresh`, {
    method: 'POST',
  });
export type FundSendReceipt = {
  request_id: string; recipient: string; subject: string;
  status: 'pending' | 'sent' | 'uncertain'; created_at: string;
};
export type FundScheduledSend = {
  request_id: string; counterparty_id: string; recipient: string; subject: string; cc: string;
  send_at: string; status: 'pending' | 'claimed' | 'sent' | 'uncertain' | 'cancelled';
};
export type FundScheduledDetail = FundScheduledSend & {
  body: string; reply_message_id: string; attachment_names: string[];
  calendar_invite: { summary: string; start: string } | null;
  tracking_enabled: boolean;
};
export type FundNote = {
  note_id: string; title: string; body: string; author_email: string;
  created_at: string; updated_at: string;
};
export const getFundSent = (id: string) =>
  request<{ messages: FundSendReceipt[] }>(`/fund/contacts/${encodeURIComponent(id)}/sent`);
export const fundDocumentUrl = (id: string) => `/api/fund/documents/${encodeURIComponent(id)}`;
export const sendFundEmail = (id: string, draft: {
  request_id: string; to: string; subject: string; body: string;
  cc?: string; reply_message_id?: string;
}) => request<{ sent: boolean; to: string; request_id: string }>(
  `/fund/contacts/${encodeURIComponent(id)}/send`, {
    method: 'POST', body: JSON.stringify(draft),
  });
export const polishFundEmail = (id: string, subject: string, body: string, mode: 'polish' | 'refine') =>
  request<{ subject: string; body: string; changes: string; review_only: boolean }>(
    `/fund/contacts/${encodeURIComponent(id)}/polish`, {
      method: 'POST', body: JSON.stringify({ subject, body, mode }),
    });
export async function saveFundProviderDraft(id: string, draft: {
  request_id: string; to: string; subject: string; body: string; cc: string;
  reply_message_id: string; document_ids: string[]; files: File[];
}): Promise<{ saved: boolean; draft_id: string; provider: string }> {
  const form = new FormData();
  for (const key of ['request_id', 'to', 'subject', 'body', 'cc', 'reply_message_id'] as const) form.append(key, draft[key]);
  form.append('document_ids', JSON.stringify(draft.document_ids));
  for (const file of draft.files) form.append('files', file);
  const response = await fetch(`/api/fund/contacts/${encodeURIComponent(id)}/save-provider-draft`, {
    method: 'POST', credentials: 'include', body: form,
  });
  if (!response.ok) {
    const result = await response.json().catch(() => null);
    throw new Error(result?.detail || `Could not save provider draft (${response.status})`);
  }
  return response.json();
}
export const getFundScheduled = (id: string) =>
  request<{ messages: FundScheduledSend[] }>(`/fund/contacts/${encodeURIComponent(id)}/scheduled`);
export const getAllFundScheduled = () =>
  request<{ messages: FundScheduledSend[] }>('/fund/scheduled');
export const getFundScheduledDetail = (requestId: string) =>
  request<FundScheduledDetail>(`/fund/scheduled/${encodeURIComponent(requestId)}`);
export const editFundScheduled = (requestId: string, draft: {
  subject: string; body: string; cc: string; send_at: string;
}) => request<{ updated: boolean }>(`/fund/scheduled/${encodeURIComponent(requestId)}`, {
  method: 'PUT', body: JSON.stringify(draft),
});
export const cancelFundScheduled = (requestId: string) =>
  request<{ cancelled: boolean }>(`/fund/scheduled/${encodeURIComponent(requestId)}`, {
    method: 'DELETE',
  });
export async function submitFundEmail(id: string, draft: {
  request_id: string; to: string; subject: string; body: string;
  cc: string; reply_message_id: string; document_ids: string[];
  files: File[]; send_at?: string; tracking?: boolean;
  calendar_start?: string; calendar_end?: string;
  calendar_summary?: string; calendar_location?: string;
}): Promise<{ sent?: boolean; scheduled?: boolean; request_id: string }> {
  const form = new FormData();
  for (const key of ['request_id', 'to', 'subject', 'body', 'cc', 'reply_message_id'] as const) {
    form.append(key, draft[key]);
  }
  form.append('document_ids', JSON.stringify(draft.document_ids));
  for (const file of draft.files) form.append('files', file);
  if (draft.send_at) form.append('send_at', draft.send_at);
  form.append('tracking', String(!!draft.tracking));
  for (const key of ['calendar_start', 'calendar_end', 'calendar_summary', 'calendar_location'] as const) {
    form.append(key, draft[key] || '');
  }
  const action = draft.send_at ? 'schedule-composed' : 'send-composed';
  const response = await fetch(`/api/fund/contacts/${encodeURIComponent(id)}/${action}`, {
    method: 'POST', credentials: 'include', body: form,
  });
  if (!response.ok) {
    const result = await response.json().catch(() => null);
    throw new Error(result?.detail || `Could not ${draft.send_at ? 'schedule' : 'send'} email (${response.status})`);
  }
  return response.json();
}
export const getFundEmailTracking = (id: string) =>
  request<{ messages: { tracking_id: string; recipient: string; subject: string; opens: number; last_opened_at: string | null; created_at: string }[] }>(
    `/fund/contacts/${encodeURIComponent(id)}/tracking`);
/** Review-only assistant turn about one confirmed investor. Grounded in that
 *  investor's synced mail, like the brief and the draft; it never sends. */
export const fundChat = (id: string, messages: { role: string; content: string }[]) =>
  request<{ reply: string; evidence_count: number; review_only: boolean }>(
    `/fund/contacts/${encodeURIComponent(id)}/chat`, {
      method: 'POST', body: JSON.stringify({ messages }),
    });
export const generateFundOutput = (id: string, kind: 'brief' | 'draft', instruction = '', documentIds: string[] = []) =>
  request<{ kind: 'brief' | 'draft'; text: string; evidence_count: number; document_sources: FundOutputSource[]; review_only: boolean }>(
    `/fund/contacts/${encodeURIComponent(id)}/generate`, {
      method: 'POST', body: JSON.stringify({ kind, instruction, document_ids: documentIds }),
    });
export const clearOwnFundContext = (id: string) =>
  request<{ ok: boolean }>(`/fund/contacts/${encodeURIComponent(id)}/context`, { method: 'DELETE' });
export async function uploadFundDocument(file: File, category: FundDocument['category']): Promise<{ ok: boolean; document_id: string }> {
  const body = new FormData();
  body.append('file', file);
  body.append('category', category);
  const response = await fetch('/api/fund/documents', { method: 'POST', credentials: 'include', body });
  if (!response.ok) {
    const result = await response.json().catch(() => null);
    throw new Error(result?.detail || `Upload failed (${response.status})`);
  }
  return response.json();
}
export const confirmFundRole = (candidate: Pick<FundCandidate, 'email' | 'name'>, role: FundRole) =>
  request<{ ok: boolean; counterparty_id: string }>('/fund/contacts/confirm', {
    method: 'POST', body: JSON.stringify({ email: candidate.email, name: candidate.name, role }),
  });
export const setFundStage = (id: string, stage: string | null) =>
  request<{ stage: string | null }>(`/fund/contacts/${encodeURIComponent(id)}/stage`, {
    method: 'PUT', body: JSON.stringify({ stage }),
  });
export const resumeFundAutoStage = (id: string) =>
  request<{ ok: boolean }>(`/fund/contacts/${encodeURIComponent(id)}/stage/auto`, { method: 'POST' });
export const setFundFollowUp = (id: string, nextStep: string, followUpOn: string | null) =>
  request<{ next_step: string | null; follow_up_on: string | null }>(
    `/fund/contacts/${encodeURIComponent(id)}/follow-up`, {
      method: 'PUT', body: JSON.stringify({ next_step: nextStep, follow_up_on: followUpOn }),
    });
export const setFundInstitutionStage = (id: string, stage: string | null) =>
  request<{ stage: string | null }>(`/fund/institutions/${encodeURIComponent(id)}/stage`, {
    method: 'PUT', body: JSON.stringify({ stage }),
  });
export const resumeFundInstitutionAutoStage = (id: string) =>
  request<{ ok: boolean }>(`/fund/institutions/${encodeURIComponent(id)}/stage/auto`, { method: 'POST' });
export const setFundInstitutionFollowUp = (id: string, nextStep: string, followUpOn: string | null) =>
  request<{ next_step: string | null; follow_up_on: string | null }>(
    `/fund/institutions/${encodeURIComponent(id)}/follow-up`, {
      method: 'PUT', body: JSON.stringify({ next_step: nextStep, follow_up_on: followUpOn }),
    });
export const createFundInstitution = (name: string) =>
  request<{ institution_id: string }>('/fund/institutions', {
    method: 'POST', body: JSON.stringify({ name }),
  });
export const getFundInstitutionActivity = (id: string) =>
  request<{ messages: FundInstitutionMessage[] }>(`/fund/institutions/${encodeURIComponent(id)}/activity`);
export const assignFundInstitution = (id: string, institutionId: string | null) =>
  request<{ institution_id: string | null }>(`/fund/contacts/${encodeURIComponent(id)}/institution`, {
    method: 'PUT', body: JSON.stringify({ institution_id: institutionId }),
  });
